import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { X, Search, Library as LibraryIcon } from "lucide-react";

const LIB_ORDER = [
  ["fabric", "Fabrics"],
  ["zipper", "Zippers"],
  ["button", "Buttons"],
  ["label", "Labels & Tickets"],
  ["print", "Prints"],
  ["bulk_colorway", "Bulk Colorways"],
];

/**
 * Modal picker that lets the user select an item from any of the material libraries.
 * onPick receives (item, libraryType). Item shape mirrors the /libraries API response.
 */
export default function LibraryPicker({ open, onClose, onPick, defaultType = "fabric", allowedTypes = null }) {
  const tabs = allowedTypes ? LIB_ORDER.filter(([k]) => allowedTypes.includes(k)) : LIB_ORDER;
  const [tab, setTab] = useState(defaultType);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => { if (open) setTab(defaultType); }, [open, defaultType]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/libraries/${tab}/items`).then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
  }, [tab, open]);

  if (!open) return null;

  const q = query.trim().toLowerCase();
  const filtered = !q ? items : items.filter((it) => JSON.stringify(it.attributes || {}).toLowerCase().includes(q) || (it.display?.name || "").toLowerCase().includes(q));

  return (
    <div className="fixed inset-0 z-[60] bg-zinc-950/60 flex items-center justify-center p-4" data-testid="library-picker">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LibraryIcon size={16} className="text-zinc-500" />
            <h2 className="font-heading text-lg font-bold">Pick from library</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500" data-testid="library-picker-close"><X size={16} /></button>
        </div>
        <div className="border-b border-zinc-200 flex gap-1 overflow-x-auto px-3">
          {tabs.map(([k, label]) => (
            <button key={k} data-testid={`picker-tab-${k}`} onClick={() => setTab(k)}
              className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${tab === k ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-950"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="px-5 pt-4">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-3 text-zinc-400" />
            <input data-testid="library-picker-search" placeholder="Search…" value={query} onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
          </div>
        </div>
        <div className="p-5 overflow-y-auto flex-1">
          {loading && <p className="text-xs text-zinc-400">Loading…</p>}
          {!loading && filtered.length === 0 && <p className="text-xs text-zinc-400 text-center py-10">No items in this library yet.</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {filtered.map((it) => (
              <button key={it.id} data-testid={`picker-card-${it.id}`}
                onClick={() => { onPick(it, tab); onClose(); }}
                className="text-left border border-zinc-200 rounded-md p-3 hover:border-zinc-950 hover:bg-zinc-50 transition-colors flex items-center gap-3">
                {it.display?.hex ? (
                  <span className="w-8 h-8 rounded border border-zinc-200 shrink-0" style={{ backgroundColor: it.display.hex }} />
                ) : (
                  <span className="w-8 h-8 rounded bg-zinc-100 border border-zinc-200 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{it.display?.name}</p>
                  {it.display?.subtitle && <p className="text-[11px] text-zinc-500 truncate">{it.display.subtitle}</p>}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Given a library item + type, extract common BOM/cost fields:
 * { product, composition, notes, unit_cost, unit }
 */
export function libraryItemToBom(item, libType) {
  const a = item.attributes || {};
  const name = item.display?.name || a.fabric_name || a.product_name || a.colorway_name || a.print_name || item.name || "Untitled";

  const composition = (() => {
    if (libType === "fabric") {
      const parts = [];
      if (a.fiber1) parts.push(`${a.fiber1}${a.fiber1_pct ? ` ${a.fiber1_pct}%` : ""}`);
      if (a.fiber2) parts.push(`${a.fiber2}${a.fiber2_pct ? ` ${a.fiber2_pct}%` : ""}`);
      if (parts.length) return parts.join(" / ");
      return a.category || "";
    }
    if (libType === "zipper") return [a.chain_type, a.gauge, a.element_finish].filter(Boolean).join(" · ");
    if (libType === "button") return [a.body_material, a.button_type, a.ligne].filter(Boolean).join(" · ");
    if (libType === "label") return [a.label_type, a.base_material].filter(Boolean).join(" · ");
    if (libType === "print") return [a.category, a.repeat_type, a.total_color_count].filter(Boolean).join(" · ");
    if (libType === "bulk_colorway") return [a.pantone_name, a.pantone_code].filter(Boolean).join(" · ");
    return "";
  })();

  const notesParts = [];
  if (a.supplier_name) notesParts.push(`Supplier: ${a.supplier_name}`);
  if (a.supplier_code) notesParts.push(`Ref: ${a.supplier_code}`);
  if (a.internal_id) notesParts.push(`ID: ${a.internal_id}`);
  if (a.weight_gsm) notesParts.push(`${a.weight_gsm} gsm`);

  const unit = ({
    fabric: (a.unit_of_measure === "Per Meter" ? "m" : a.unit_of_measure === "Per Yard" ? "yd" : a.unit_of_measure === "Per Kg" ? "kg" : "m"),
    zipper: "pc",
    button: "pc",
    label: "pc",
    print: "m",
    bulk_colorway: "kg",
  })[libType] || "pc";

  return {
    product: name,
    composition,
    notes: notesParts.join(" · "),
    unit_cost: a.actual_cost || a.target_cost || 0,
    unit,
    library_item_id: item.id,
    library_type: libType,
  };
}
